import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject } from 'rxjs';
import { concatAll, groupBy, map, mergeMap, switchMap, take, takeUntil, toArray } from 'rxjs/operators';
import { ICustomCaseSearchOptions, ICustomCaseSearchResult } from 'src/app/reports/reports-models';
import { AppService } from 'src/app/services/app.service';
import { CaseSearchService } from 'src/app/services/case-search.service';
import { CasesService } from 'src/app/services/cases.service';
import { LocalizationService, LocalizedNumberFormat } from 'src/app/shared/localization/localization.service';
import { ICaseViewModel, IDebtorStats } from 'src/app/shared/models';
import { RxHook } from 'src/app/shared/RxHook';

@Component({
  selector: 'app-case-debtor',
  templateUrl: './case-debtor.component.html',
  styleUrls: ['./case-debtor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RxHook]
})
export class CaseDebtorComponent implements OnInit, OnDestroy {
  @Input() case: ICaseViewModel;
  @Input() debtorStats: IDebtorStats;
  @ViewChild('relatedCasesPopup') relatedCasesPopup: TemplateRef<any>;

  SEARCH_CUSTOM_VALUE_NULL_STRING = '<NULL>'

  debtorRelatedCasesOnlyActive: boolean = true;
  referencesRelatedCasesOnlyActive: boolean = true;

  numberFormat: LocalizedNumberFormat;
  dateFormat: string;
  language: string;

  data;
  dialogRef: MatDialogRef<any>;

  private readonly unsubscribe$ = new Subject();

  get isEnglish() {
    return this.translateService.getDefaultLang().toLocaleLowerCase() == 'en';
  };

  constructor(
    public localizationService: LocalizationService
    , private rxHook: RxHook
    , private search: CaseSearchService
    , public dialog: MatDialog
    , public translateService: TranslateService
    , private router: Router
    , private caseService: CasesService
    , private appService: AppService) {
  }

  ngOnInit(): void {
    this.localizationService.settings$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(_ => {
        this.numberFormat = _.numberPipeFormat;
        this.dateFormat = _.datePipeFormat;
      });
  }


  stringAsDateOnly(dateToParse : string)
  {
    if(dateToParse == null || !dateToParse.includes('T') || !dateToParse.includes('-')) return dateToParse;

    const [date, _] = dateToParse.split('T');
    const [year, month, day] = date.split('-').map(Number);

    return new Date(year, month -1 , day);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  getCasesWithSameDebtorHook() {
    const caseSearchOptions = {
      debtorId: this.case.debtor.id
      , activeOrClosed: this.debtorRelatedCasesOnlyActive ? 'Active' : 'NA'
      , caseSearchColumns: ['CaseNumber', 'CustomerReference', 'RestCapital', 'RestTotal', 'StatusText', 'StatusTextEn']
    } as ICustomCaseSearchOptions;

    return this.rxHook.use(this.search.customCaseSearchAllCases(caseSearchOptions));
  }


  getCasesWithSameReferencesHook() {
    if (this.stringIsNullOrEmpty(this.case.reference1) && this.stringIsNullOrEmpty(this.case.reference2))
      return this.rxHook.use(of([]));

    const caseCustomer$ = this.appService.customers$.pipe(map(_ => _.find(x => x.number == this.case.customer.number)));

    let search$ = caseCustomer$.pipe(
      take(1),
      switchMap(_ => {
      const caseSearchOptions = {
          reference1: this.stringIsNullOrEmpty(this.case.reference1) ? this.SEARCH_CUSTOM_VALUE_NULL_STRING : this.case.reference1
        , reference2: this.stringIsNullOrEmpty(this.case.reference2) ? this.SEARCH_CUSTOM_VALUE_NULL_STRING : this.case.reference2
        , activeOrClosed: this.referencesRelatedCasesOnlyActive ? 'Active' : 'NA'
        , customerOrganizationNumber: _.organizationNumber
        , caseSearchColumns: ['CaseNumber', 'CustomerReference1', 'CustomerReference2', 'RestCapital', 'RestTotal', 'StatusText', 'StatusTextEn']
      } as ICustomCaseSearchOptions;
  
      return this.search.customCaseSearchAllCases(caseSearchOptions)
    }));

    return this.rxHook.use(search$);
  }

  onOpenRelatedCasesPopup() {
    this.data = {
      sameDebtorData: this.getCasesWithSameDebtorHook(),
      sameReferenceData: this.getCasesWithSameReferencesHook(),
      sameReferenceAddress: this.getAddressesWithSameReferencesHook(true)
    }

    this.dialogRef = this.dialog.open(this.relatedCasesPopup, { data: this.data, minHeight: '200px', minWidth: '1000px' });
    this.dialogRef.afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => { this.dialogRef = null })
  }

  private getAddressesWithSameReferencesHook(activeCases : boolean) {
    let request$ = this.caseService.getAddressesWithSameReferences(this.case.reference1, this.case.reference2, activeCases).pipe(map(addresses => 
      this.processAddresses(addresses)
    ));

    return this.rxHook.use(request$); 
  }

  processAddresses(addresses: any[]) {
    const groupedAddresses = addresses.reduce((accumulator, current) => {
      const address = current.address;

      if (!accumulator[address]) {
        accumulator[address] = {
          measurePointId: current.measurePointId,
          caseNumber: [],
          address: address,
          post: current.post,
          city: current.city
        };
      }

      if (!accumulator[address].caseNumber.includes(current.caseNumber)) {
        accumulator[address].caseNumber.push(current.caseNumber);
      }

      return accumulator;
    }, {});

    return Object.keys(groupedAddresses).map(key => ({
      measurePointId: groupedAddresses[key].measurePointId,
      caseNumber: groupedAddresses[key].caseNumber.join(', '),
      address: groupedAddresses[key].address,
      post: groupedAddresses[key].post,
      city: groupedAddresses[key].city
    })); 
  }

  onRelatedCasesToggleChange(type: RelatedCasesType) {
    if (type == 'Debtor')
      this.data.sameDebtorData = this.getCasesWithSameDebtorHook();
    else if (type == 'Reference')
      this.data.sameReferenceData = this.getCasesWithSameReferencesHook();
      this.data.sameReferenceAddress = this.getAddressesWithSameReferencesHook(this.referencesRelatedCasesOnlyActive);
  }

  openCase(caseNumber: number) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/cases/${caseNumber}`])
    );
    window.open(url, '_blank');
  }

  getTotals(value: ICustomCaseSearchResult[]) {
    return value?.reduce((prev, current) => {
      return {
        restCapitals: prev.restCapitals + current.restCapital,
        restCosts: prev.restCosts + (current.restTotal - current.restCapital),
        restTotals: prev.restTotals + current.restTotal
      }
    }, ({ restCapitals: 0, restCosts: 0, restTotals: 0 }))
  }


  private stringIsNullOrEmpty(value: string) {
    return value == null || value.trim() == '';
  }
}

export type RelatedCasesType = 'Reference' | 'Debtor';
